import { motion } from "framer-motion";
import { MessageCircle, Calendar } from "lucide-react";

const STUDIO_SETUP_IMAGE = "https://customer-assets.emergentagent.com/job_podcast-savi-refresh/artifacts/cn20xiko_20251222_135858-EDIT.jpg";
const MIC_IMAGE = "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxtb29keSUyMGRhcmslMjBwb2RjYXN0JTIwbWljcm9waG9uZSUyMHN0dWRpb3xlbnwwfHx8fDE3ODQ0NzE0OTR8MA&ixlib=rb-4.1.0&q=85";

export const StudioRental = () => {
  const equipment = [
    { label: "Sony ZV-E10 Cameras ×3", description: "Multi-angle 4K video" },
    { label: "Insta360 GO 3S", description: "4K action cam, creative angles" },
    { label: "Shure MV7 Microphones", description: "Broadcast-quality audio" },
    { label: "RØDE Wireless Micro", description: "Compact wireless freedom" },
    { label: "Rodecaster Pro II", description: "Professional mixing & recording" },
    { label: "Studio Lighting", description: "Professional & RGB colour" },
  ];

  const useCases = [
    "Podcasts & Interviews",
    "Band Album Covers",
    "Music Videos",
    "Content Creation",
    "Product Shoots",
    "Documentary Filming",
  ];

  return (
    <section className="px-5 sm:px-8 lg:px-12 py-14 sm:py-20 md:py-32 bg-[#10101A]" data-testid="studio-rental-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Content */}
        <div className="min-w-0">
          <p className="overline-tcc mb-4">Rent the room</p>
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[0.9] text-[#F4F2EE] mb-8 break-words">
            Your story.<br />
            <span className="text-stroke">Our studio.</span>
          </h2>

          <div className="space-y-4 text-[#9494A6] font-light text-base leading-relaxed max-w-lg mb-8">
            <p>
              The same space where we record Truths, Chaos &amp; Cupcakes is available for your
              creative project — whether that's a podcast, music video, band shoot, or content
              that needs a setting with soul.
            </p>
            <p>
              Set in the mountains of Valencia with natural light, original art on every wall,
              and an atmosphere that can't be faked. Professional equipment included.
            </p>
          </div>

          {/* Use cases */}
          <div className="flex flex-wrap gap-2 mb-12">
            {useCases.map((useCase, index) => (
              <span
                key={index}
                className="font-mono-tcc text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#323248] text-[#9494A6]"
              >
                {useCase}
              </span>
            ))}
          </div>

          {/* Equipment spec sheet */}
          <p className="font-mono-tcc text-xs uppercase tracking-[0.25em] text-[#C8FF3E] mb-4">
            Spec sheet — included
          </p>
          <div className="border-t border-[#323248] mb-12">
            {equipment.map((item, index) => (
              <div
                key={index}
                className="flex flex-wrap items-baseline gap-x-5 gap-y-1 py-3.5 border-b border-[#323248] hover:bg-[#181824] transition-colors duration-150 px-2"
              >
                <span className="font-mono-tcc text-xs text-[#C8FF3E] shrink-0">
                  0{index + 1}
                </span>
                <span className="text-[#F4F2EE] text-sm font-medium flex-1 min-w-0">{item.label}</span>
                <span className="font-mono-tcc text-[10px] uppercase tracking-widest text-[#5E5E72] hidden sm:block">
                  {item.description}
                </span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a
              href="https://wa.me/34632628051?text=Hi%20Steve,%20I'm%20interested%20in%20renting%20the%20studio%20at%20Savi%20for%20a%20creative%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brutal-pink"
              data-testid="studio-rental-whatsapp"
            >
              <MessageCircle size={14} />
              Message Steve for a quote
            </a>
            <a
              href="https://calendly.com/savicoliving/studio-tour"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brutal-ghost"
              data-testid="studio-rental-calendar"
            >
              <Calendar size={14} />
              Book a studio tour
            </a>
          </div>
        </div>

        {/* Images */}
        <div className="flex flex-col gap-6 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="group border border-[#323248] overflow-hidden"
          >
            <img
              src={STUDIO_SETUP_IMAGE}
              alt="Creative studio at Savi Coliving — recording and filming setup"
              className="w-full h-[260px] sm:h-[380px] object-cover img-brutal"
              data-testid="studio-rental-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="group border border-[#323248] overflow-hidden ml-8 sm:ml-16 md:ml-24"
          >
            <img
              src={MIC_IMAGE}
              alt="Studio microphone"
              className="w-full h-[200px] sm:h-[280px] object-cover img-brutal"
            />
          </motion.div>
          <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#5E5E72] text-right">
            Located at Savi Coliving — Valencia, ES
          </p>
        </div>
      </div>
    </section>
  );
};
